import React, { useState } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import AnimatedPage from '@/components/AnimatedPage';
import UsersTable from '@/pages/users/components/UsersTable';
import UserForm from '@/pages/users/components/UserForm';
import ConfirmDialog from '@/pages/users/components/ConfirmDialog';
import SearchBar from '@/pages/users/components/SearchBar';
import { useUsers } from '@/pages/users/hooks/useUsers';
import { useUserForm } from '@/pages/users/hooks/useUserForm';
import { useFilterAndSort } from '@/pages/users/hooks/useFilterAndSort';
import { useConfirmDialog } from '@/pages/users/hooks/useConfirmDialog';
import { canPerformAction, ActionKey } from '@/auth/actionMatrix';
import { RoleEnum } from '@/types/auth/roles';

const Users = () => {
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const currentUser = useSelector((state: RootState) => state.auth.user);
  
  const {
    users,
    loading,
    error: usersError,
    updateUser,
    deleteUser,
    toggleUserActive,
    toggleUserSuperuser,
    resetUser2fa,
  } = useUsers();

  const {
    open: formOpen,
    editingUser,
    formData,
    error: formError,
    handleOpen: handleFormOpen,
    handleClose: handleFormClose,
    validateForm,
    setFormData,
    setError: setFormError,
  } = useUserForm();

  const {
    filteredUsers,
    searchQuery,
    setSearchQuery,
    orderBy,
    order,
    handleSort,
  } = useFilterAndSort(users);
  const displayedUsers = filteredUsers.filter((user) => (!roleFilter || user.role === roleFilter) && (!activeFilter || user.is_active === (activeFilter === 'active')));

  const {
    open: confirmOpen,
    message: confirmMessage,
    handleOpen: handleConfirmOpen,
    handleClose: handleConfirmClose,
    handleConfirm: handleConfirmAction,
  } = useConfirmDialog();

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!editingUser) return;
    try {
      const saved = await updateUser(editingUser.id, formData);
      if (saved) handleFormClose();
    } catch (err) {
      console.error('Error submitting user:', err);
    }
  };

  const handleDelete = (id: string) => {
    if (!canPerformAction(currentUser, ActionKey.USERS_DELETE)) {
      setFormError('У вас нет прав для выполнения этой операции');
      return;
    }

    handleConfirmOpen(`Вы уверены, что хотите удалить пользователя ${users.find(u => u.id === id)?.username}?`, async () => {
      await deleteUser(id);
    });
  };

  const handleToggleActive = (id: string, newState: boolean) => {
    if (!canPerformAction(currentUser, ActionKey.USERS_TOGGLE_ACTIVE)) {
      setFormError('У вас нет прав для выполнения этой операции');
      return;
    }

    if (id === currentUser?.id) {
      setFormError('Нельзя изменить собственный статус активности');
      return;
    }

    handleConfirmOpen(
      `Вы уверены, что хотите ${newState ? 'активировать' : 'деактивировать'} этого пользователя?`,
      async () => {
        await toggleUserActive(id, newState);
      }
    );
  };

  const handleToggleSuperuser = (id: string, newState: boolean) => {
    if (!canPerformAction(currentUser, ActionKey.USERS_TOGGLE_SUPERUSER)) {
      setFormError('У вас нет прав для выполнения этой операции');
      return;
    }

    if (id === currentUser?.id) {
      setFormError('Нельзя изменить собственный статус администратора');
      return;
    }

    handleConfirmOpen(
      `Вы уверены, что хотите ${newState ? 'назначить' : 'снять'} права администратора для этого пользователя?`,
      async () => {
        await toggleUserSuperuser(id, newState);
      }
    );
  };

  const handleReset2fa = (user: typeof users[number]) => {
    handleConfirmOpen(
      `Отключить 2FA пользователя ${user.username}? TOTP-секрет и резервные коды будут удалены, а доверенные устройства отозваны.`,
      async () => {
        await resetUser2fa(user.id);
      },
    );
  };

  return (
    <AnimatedPage>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Пользователи
          </Typography>
        </Box>

        {(usersError || formError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {usersError || formError}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}><Box sx={{ flex: 1 }}><SearchBar value={searchQuery} onChange={setSearchQuery} /></Box><FormControl sx={{ minWidth: 220 }}><InputLabel>Роль</InputLabel><Select value={roleFilter} label="Роль" onChange={(event) => setRoleFilter(event.target.value)}><MenuItem value="">Все</MenuItem>{Object.values(RoleEnum).map((role) => <MenuItem key={role} value={role}>{role}</MenuItem>)}</Select></FormControl><FormControl sx={{ minWidth: 180 }}><InputLabel>Активность</InputLabel><Select value={activeFilter} label="Активность" onChange={(event) => setActiveFilter(event.target.value)}><MenuItem value="">Все</MenuItem><MenuItem value="active">Активные</MenuItem><MenuItem value="inactive">Неактивные</MenuItem></Select></FormControl><Button onClick={() => { setSearchQuery(''); setRoleFilter(''); setActiveFilter(''); }}>Сбросить</Button></Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <UsersTable
            users={displayedUsers}
            currentUser={currentUser}
            onEdit={handleFormOpen}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onToggleSuperuser={handleToggleSuperuser}
            onReset2fa={handleReset2fa}
            orderBy={orderBy}
            order={order}
            onSort={handleSort}
          />
        )}

        <UserForm
          open={formOpen}
          editingUser={editingUser}
          formData={formData}
          error={formError}
          onClose={handleFormClose}
          onSubmit={handleSubmit}
          onChange={setFormData}
        />

        <ConfirmDialog
          open={confirmOpen}
          message={confirmMessage}
          onClose={handleConfirmClose}
          onConfirm={handleConfirmAction}
        />
      </Box>
    </AnimatedPage>
  );
};

export default Users;
